/** Notice when work arrives from outside the editor.
 *
 * The plan calls an external paste the cheapest high-value integrity signal
 * there is: a 300-line paste ninety seconds in tells a reviewer more than any
 * heuristic. It is also the one thing no server can see — the clipboard never
 * leaves the browser — so unlike every other event this is reported by the
 * client and is therefore best-effort. A candidate who disables it produces a
 * session with no paste events at all, which is itself worth a reviewer
 * noticing, but absence is not proof.
 *
 * Internal versus external is decided by remembering what was last copied or
 * cut inside this page. Anything else came from somewhere we cannot see.
 */
import type { SessionApi } from "./api";

// Below this, a paste is a word or a symbol and says nothing useful.
const MIN_INTERESTING_CHARS = 40;

let lastInternalCopy = "";

async function sha256(text: string): Promise<string> {
  try {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return ""; // insecure context; the rest of the event is still worth having
  }
}

export function watchClipboard(api: SessionApi, currentPath: () => string | null): () => void {
  function remember(event: ClipboardEvent) {
    const text = event.clipboardData?.getData("text") ?? window.getSelection()?.toString() ?? "";
    if (text) lastInternalCopy = text;
  }

  async function onPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData("text") ?? "";
    if (text.length < MIN_INTERESTING_CHARS) return;

    const source = text === lastInternalCopy ? "internal" : "external";
    try {
      await api.reportEvent("editor.paste", {
        path: currentPath() ?? "(terminal)",
        chars: text.length,
        lines: text.split("\n").length,
        source,
        content_sha: await sha256(text),
      });
    } catch {
      // Never let recording break the candidate's paste.
    }
  }

  document.addEventListener("copy", remember, true);
  document.addEventListener("cut", remember, true);
  document.addEventListener("paste", onPaste, true);
  return () => {
    document.removeEventListener("copy", remember, true);
    document.removeEventListener("cut", remember, true);
    document.removeEventListener("paste", onPaste, true);
  };
}
